/**
 * RuinModule — comb-resonator "ruin filter". Short delay (1–30 ms)
 * fed back into itself produces a tuned harmonic series — Karplus-
 * Strong style, but driven by whatever's coming through it instead
 * of a noise burst. Below ~20 ms feedback the comb tunes into the
 * audible pitch range; above that, you get short echo / metallic
 * ringing.
 *
 * Graph:
 *
 *   input ─┬─ dryGain ────────────────────────────────────────── outputMix ─►
 *          │
 *          └─► combIn ──► delay ──► dampLP ──► combOut ─► wetGain ─► outputMix
 *                          ▲                       │
 *                          └─── feedback ◄─────────┘
 *
 * Pitch (`Tune`) is exposed in Hz so users can land the comb on
 * harmonic intervals (220 Hz = open A, etc.). Feedback is bounded
 * to [0, 0.97] — anything higher self-oscillates indefinitely and
 * eats CPU + speakers. A lowpass in the feedback loop dampens the
 * runaway high frequencies that comb feedback amplifies; tuning
 * Damp lets you choose between a metallic "ping" (high cutoff) and
 * a softer wood-block (low cutoff).
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

/** Maximum delay in seconds — bounds the worst-case memory of the
 *  Web Audio DelayNode allocation. 30 ms covers the audible pitch
 *  floor (~33 Hz) plus a small margin. */
const MAX_DELAY_SEC = 0.03;
/** Pitch range — corresponds to 1 / MAX_DELAY_SEC and 1 / MIN_DELAY_SEC. */
const MIN_TUNE_HZ = 50;
const MAX_TUNE_HZ = 2000;
/** Feedback cap — past this the loop self-amplifies forever. */
const MAX_FEEDBACK = 0.97;

export class RuinModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'ruin' as const;
  readonly displayName = 'RUIN';
  readonly id: string;
  bypass = false;

  readonly tune = new NumericParameter({
    name: 'Tune',
    default: 220,
    min: MIN_TUNE_HZ,
    max: MAX_TUNE_HZ,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 30,
    randomSkew: 0.6,
    formatter: (v) =>
      v >= 1000 ? `${(v / 1000).toFixed(2)}k` : `${v.toFixed(0)}`,
  });
  readonly feedback = new NumericParameter({
    name: 'Feedback',
    default: 0.6,
    min: 0,
    max: MAX_FEEDBACK,
    curve: 'linear',
    smoothingMs: 30,
    randomSkew: 0.6,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  readonly damp = new NumericParameter({
    name: 'Damp',
    default: 4000,
    min: 200,
    max: 12000,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 30,
    randomSkew: 1.5,
    formatter: (v) =>
      v >= 1000 ? `${(v / 1000).toFixed(2)}k` : `${v.toFixed(0)}`,
  });
  readonly mix = new NumericParameter({
    name: 'Mix',
    default: 0.4,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    // Bias low — high mix on a heavy comb is overpowering.
    randomSkew: 0.5,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });

  readonly parameters: readonly AnyParameter[] = [
    this.tune,
    this.feedback,
    this.damp,
    this.mix,
  ];

  private engine: AudioEngine | null = null;
  private inputNode: GainNode | null = null;
  private outputMixNode: GainNode | null = null;
  private combIn: GainNode | null = null;
  private combOut: GainNode | null = null;
  private delay: DelayNode | null = null;
  private feedbackGain: GainNode | null = null;
  private dampLP: BiquadFilterNode | null = null;
  private dryGain: GainNode | null = null;
  private wetGain: GainNode | null = null;
  private started = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(id?: string) {
    this.id = id ?? mintId('ruin');
  }

  get input(): AudioNode | null {
    return this.inputNode;
  }
  get output(): AudioNode | null {
    return this.outputMixNode;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;

    this.inputNode = ctx.createGain();
    this.outputMixNode = ctx.createGain();
    this.combIn = ctx.createGain();
    this.combOut = ctx.createGain();

    this.delay = ctx.createDelay(MAX_DELAY_SEC);
    this.delay.delayTime.value = this.tuneToDelay(this.tune.value);

    this.dampLP = ctx.createBiquadFilter();
    this.dampLP.type = 'lowpass';
    this.dampLP.frequency.value = this.damp.value;
    this.dampLP.Q.value = 0.3;

    this.feedbackGain = ctx.createGain();
    this.feedbackGain.gain.value = this.feedback.value;

    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();

    // Wet path: input → combIn → delay → damp → combOut → wetGain
    //                    ▲           │
    //                    └─ feedback ┘
    this.inputNode.connect(this.combIn);
    this.combIn.connect(this.delay);
    this.delay.connect(this.dampLP);
    this.dampLP.connect(this.combOut);
    this.combOut.connect(this.wetGain);
    this.wetGain.connect(this.outputMixNode);

    // Feedback loop closes from combOut back into combIn.
    this.combOut.connect(this.feedbackGain);
    this.feedbackGain.connect(this.combIn);

    // Dry path
    this.inputNode.connect(this.dryGain);
    this.dryGain.connect(this.outputMixNode);

    // Tune → delay time (inverse map). We bind the AudioParam target
    // via subscribe instead of bindAudioParam because the relationship
    // is non-linear (delay = 1 / freq) — the macro path would write
    // raw Hz directly into delayTime which would be wrong.
    this.unsubs.push(
      this.tune.subscribe((v) => this.applyTune(v)),
    );
    this.feedback.bindAudioParam(this.feedbackGain.gain, ctx);
    this.damp.bindAudioParam(this.dampLP.frequency, ctx);
    this.applyMix(this.mix.value, /* instant */ true);
    this.unsubs.push(this.mix.subscribe((v) => this.applyMix(v)));

    this.started = true;
  }

  /**
   * Mod-routing lookup. Feedback and Damp are direct AudioParam
   * routes; Tune is non-linear (1/freq → delayTime) so we don't
   * expose it as an AudioParam — macro path drives it via
   * NumericParameter.set instead. Mix uses gain envelopes.
   */
  getAudioParam(paramName: string): AudioParam | null {
    if (paramName === 'Feedback') return this.feedbackGain?.gain ?? null;
    if (paramName === 'Damp') return this.dampLP?.frequency ?? null;
    return null;
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    this.inputNode?.disconnect();
    this.combIn?.disconnect();
    this.combOut?.disconnect();
    this.delay?.disconnect();
    this.dampLP?.disconnect();
    this.feedbackGain?.disconnect();
    this.dryGain?.disconnect();
    this.wetGain?.disconnect();
    this.outputMixNode?.disconnect();
    this.inputNode = null;
    this.outputMixNode = null;
    this.combIn = null;
    this.combOut = null;
    this.delay = null;
    this.dampLP = null;
    this.feedbackGain = null;
    this.dryGain = null;
    this.wetGain = null;
    this.started = false;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }

  /** Convert a target frequency into a comb delay time, clamped to
   *  the DelayNode's allocated max. */
  private tuneToDelay(hz: number): number {
    const clamped = Math.max(MIN_TUNE_HZ, Math.min(MAX_TUNE_HZ, hz));
    return Math.min(MAX_DELAY_SEC, 1 / clamped);
  }

  private applyTune(hz: number): void {
    if (!this.engine || !this.delay) return;
    const t = this.engine.context.currentTime;
    const target = this.tuneToDelay(hz);
    this.delay.delayTime.cancelScheduledValues(t);
    // Smooth the change so big sweeps don't click.
    this.delay.delayTime.setTargetAtTime(target, t, 0.01);
  }

  private applyMix(mix: number, instant = false): void {
    if (!this.engine || !this.dryGain || !this.wetGain) return;
    // Equal-power crossfade — matches the rest of the FX bank.
    const dry = Math.cos((mix * Math.PI) / 2);
    const wet = Math.sin((mix * Math.PI) / 2);
    const t = this.engine.context.currentTime;
    if (instant) {
      this.dryGain.gain.setValueAtTime(dry, t);
      this.wetGain.gain.setValueAtTime(wet, t);
    } else {
      this.dryGain.gain.cancelScheduledValues(t);
      this.wetGain.gain.cancelScheduledValues(t);
      this.dryGain.gain.setTargetAtTime(dry, t, 0.02);
      this.wetGain.gain.setTargetAtTime(wet, t, 0.02);
    }
  }
}
